import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { ReportPeriod } from '@/lib/api/reports.js';

export type ReportFilters = {
  period: ReportPeriod;
  setPeriod: (p: ReportPeriod) => void;
  /** Sets period and local calendar dateFrom/dateTo together (presets). */
  selectPeriod: (p: ReportPeriod) => void;
  dateFrom: string;
  setDateFrom: (s: string) => void;
  dateTo: string;
  setDateTo: (s: string) => void;
  /** '' = all stores (admin) or session default */
  storeId: string;
  setStoreId: (id: string) => void;
  groupBy: 'day' | 'week' | 'month';
  setGroupBy: (g: 'day' | 'week' | 'month') => void;
};

const ReportFiltersContext = createContext<ReportFilters | null>(null);

/** Local YYYY-MM-DD (browser timezone) — matches HTML date inputs. */
export function localDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Local calendar ranges for report presets (not UTC). */
export function rangeForReportPeriod(period: ReportPeriod): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const today = localDateStr(now);
  if (period === 'today' || period === 'custom') {
    return { dateFrom: today, dateTo: today };
  }
  if (period === 'week') {
    const s = new Date(now);
    s.setDate(s.getDate() - 6);
    return { dateFrom: localDateStr(s), dateTo: today };
  }
  if (period === 'month') {
    return { dateFrom: localDateStr(new Date(now.getFullYear(), now.getMonth(), 1)), dateTo: today };
  }
  if (period === 'quarter') {
    const q = Math.floor(now.getMonth() / 3);
    return { dateFrom: localDateStr(new Date(now.getFullYear(), q * 3, 1)), dateTo: today };
  }
  // year
  return { dateFrom: localDateStr(new Date(now.getFullYear(), 0, 1)), dateTo: today };
}

export function ReportFiltersProvider({ children }: { children: ReactNode }) {
  const initial = rangeForReportPeriod('month');
  const [period, setPeriodState] = useState<ReportPeriod>('month');
  const [dateFrom, setDateFrom] = useState(initial.dateFrom);
  const [dateTo, setDateTo] = useState(initial.dateTo);
  const [storeId, setStoreId] = useState('');
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');

  const setPeriod = useCallback((p: ReportPeriod) => {
    setPeriodState(p);
  }, []);

  const selectPeriod = useCallback((p: ReportPeriod) => {
    setPeriodState(p);
    if (p === 'custom') {
      const today = localDateStr(new Date());
      setDateFrom((prev) => prev || today);
      setDateTo((prev) => prev || today);
    } else {
      const range = rangeForReportPeriod(p);
      setDateFrom(range.dateFrom);
      setDateTo(range.dateTo);
    }
  }, []);

  const v = useMemo(
    () =>
      ({
        period,
        setPeriod,
        selectPeriod,
        dateFrom,
        setDateFrom,
        dateTo,
        setDateTo,
        storeId,
        setStoreId,
        groupBy,
        setGroupBy,
      }) satisfies ReportFilters,
    [period, setPeriod, selectPeriod, dateFrom, dateTo, storeId, groupBy],
  );

  return <ReportFiltersContext.Provider value={v}>{children}</ReportFiltersContext.Provider>;
}

export function useReportFilters(): ReportFilters {
  const c = useContext(ReportFiltersContext);
  if (!c) throw new Error('useReportFilters must be used under ReportFiltersProvider');
  return c;
}

export function buildReportQuery(f: ReportFilters): {
  period?: ReportPeriod;
  dateFrom?: string;
  dateTo?: string;
  storeId?: string;
  groupBy?: 'day' | 'week' | 'month';
} {
  const q: {
    period?: ReportPeriod;
    dateFrom?: string;
    dateTo?: string;
    storeId?: string;
    groupBy?: 'day' | 'week' | 'month';
  } = {
    period: f.period,
    groupBy: f.groupBy,
  };
  // Always send explicit local dates so the API does not resolve "today" in UTC
  if (f.dateFrom.trim()) q.dateFrom = f.dateFrom.trim();
  if (f.dateTo.trim()) q.dateTo = f.dateTo.trim();
  if (f.storeId.trim()) q.storeId = f.storeId.trim();
  return q;
}

/** Stable query params derived from report filters (primitive deps → reliable refetches). */
export function useReportQueryParams(): {
  period?: ReportPeriod;
  dateFrom?: string;
  dateTo?: string;
  storeId?: string;
  groupBy?: 'day' | 'week' | 'month';
} {
  const { period, dateFrom, dateTo, storeId, groupBy } = useReportFilters();
  return useMemo(() => {
    const q: {
      period?: ReportPeriod;
      dateFrom?: string;
      dateTo?: string;
      storeId?: string;
      groupBy?: 'day' | 'week' | 'month';
    } = { period, groupBy };
    if (dateFrom.trim()) q.dateFrom = dateFrom.trim();
    if (dateTo.trim()) q.dateTo = dateTo.trim();
    if (storeId.trim()) q.storeId = storeId.trim();
    return q;
  }, [period, dateFrom, dateTo, storeId, groupBy]);
}
